// components/SettingsPanel.tsx
import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { SettingsPanelConfig, TempSettings, CanvasItem } from '../../types/types';
import HeadingProperties from './Properties/HeadingProperties';
import StyledText from '../StyledComponents/StyledText';
import { useTheme } from '../../middleware/ThemeContext';
import BgImage from '../../assets/images/CreateFormBackground.png'
import HeaderProperties from './Properties/HeaderProperties';
import FooterProperties from './Properties/FooterProperties';
import ParagraphProperties from './Properties/ParagraphProperties';
import SeparatorProperties from './Properties/SeparatorProperties';
import ButtonProperties from './Properties/ButtonProperties';
import MediaProperties from './Properties/MediaProperties';
import SectionProperties from './Properties/SectionProperties';
import { Button } from '../form/Button';
import CrossIcon from '../../assets/icons/CrossIcon';
import SaveIcon from '../../assets/icons/SaveIcon';
import FormProperties from './Properties/FormProperties';
import SearchQueryProperties from './Properties/SearchQueryProperties';
import { Theme } from '../../utils/themes';
import PublicDirectoryProperties from './Properties/PublicDirectoryProperties';
const Backdrop = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  animation: fadeIn 0.2s ease-in;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const RightSidebar = styled.div<{ isOpen: boolean, theme: Theme }>`
  position: fixed;
  right: ${props => props.isOpen ? '0' : '-534px'};
  top: 0;
  width: 534px;
  height: 100vh;
  background: ${props => props.theme.CorporateTheme.colors.background};
  border-left: 1px solid #e9ecef;
  overflow: hidden;
  transition: right 0.3s ease;
  display: flex;
  flex-direction: column;
  z-index: 100000;
  box-shadow: ${props => props.isOpen ? '-2px 0 8px rgba(0, 0, 0, 0.15)' : 'none'};
`;

const SidebarHeader = styled.div`
  padding: 20px;
  display: flex;
  justify-content: space-between;
  background-image:  url(${BgImage});
    background-repeat: no-repeat;
    background-position: 0px -28px;
    background-size: cover;
  align-items: center;

`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background: #f8f9fa;
    color: #212529;
  }
`;

const SidebarContent = styled.div`
  padding: 20px;
  padding-top: 0px;
  overflow-y: auto;
  flex: 1;
  max-height: calc(100vh - 73px);
`;

const SettingGroup = styled.div`
  margin-bottom: 20px;
`;

const SettingLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
`;

const SettingInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  
  &:focus {
    border-color: #6c63ff;
  }
`;

const SettingSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  background: white;
  
  &:focus {
    border-color: #6c63ff;
  }
`;

const SettingCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
  
  label {
    font-size: 13px;
    color: #495057;
    cursor: pointer;
    margin: 0;
  }
`;

const SidebarFooter = styled.div`
  padding: 20px;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 10px;
  background: white;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 10px 20px;
  background: #6c63ff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #5a52e0;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(108, 99, 255, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 10px 20px;
  background: white;
  color: #495057;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f8f9fa;
    border-color: #adb5bd;
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

interface SettingsPanelProps {
    settingsPanel: SettingsPanelConfig | null;
    setSettingsPanel: (config: SettingsPanelConfig | null, nextTempSettings?: TempSettings, options?: { skipPrompt?: boolean }) => void;
    tempSettings: TempSettings;
    setTempSettings: (settings: TempSettings) => void;
    canvasItems: CanvasItem[];
    setCanvasItems: React.Dispatch<React.SetStateAction<CanvasItem[]>>;
    pendingChanges: Map<string, TempSettings>;
    setPendingChanges: React.Dispatch<React.SetStateAction<Map<string, TempSettings>>>;
    canvasItemsSnapshot: CanvasItem[] | null;
}

const COLOR_KEYS_BY_TYPE: Record<string, string[]> = {
    heading: ['color'],
    paragraph: ['paragraphColor'],
    separator: ['separatorColor'],
    button: ['buttonColor', 'buttonTextColor', 'buttonHoverColor'],
};

const SettingsPanel: React.FC<SettingsPanelProps> = ({
    settingsPanel,
    setSettingsPanel,
    tempSettings,
    setTempSettings,
    canvasItems,
    setCanvasItems,
    pendingChanges,
    setPendingChanges,
    canvasItemsSnapshot
}) => {
    const { theme } = useTheme();
    const [isMediaUploading, setIsMediaUploading] = useState(false);

    const shouldBypassCancelPrompt = (panel: SettingsPanelConfig | null) => Boolean(panel);

    // Snapshot of tempSettings at the moment the panel opened, used to detect explicit color changes
    const initialTempSettingsRef = useRef<TempSettings>({});

    useEffect(() => {
        if (settingsPanel !== null) {
            initialTempSettingsRef.current = { ...tempSettings };
        }
    }, [settingsPanel]); // eslint-disable-line react-hooks/exhaustive-deps

    // Apply settings to pending changes and close the panel
    const handleApplySettings = () => {
        if (!settingsPanel) return;

        const isPublicDirectory = settingsPanel.itemType === 'public_directory';
        
        // Validation for public directory: ensure at least one field is selected
        if (isPublicDirectory) {
            const selectedFields = tempSettings.selectedFields || [];
            const visibleFields = Array.isArray(selectedFields) 
                ? selectedFields.filter((field: any) => {
                    const normalizedLabel = String(field.label || '').trim().toLowerCase();
                    const normalizedValue = String(field.value || '').trim().toLowerCase();
                    return normalizedLabel !== 'id' && normalizedValue !== 'id' && normalizedValue !== 'contact_id' && normalizedValue !== 'contactid';
                })
                : [];
            
            if (visibleFields.length === 0) {
                // Show validation error - will be displayed in the PublicDirectoryProperties component
                const event = new CustomEvent('showFieldValidationError');
                window.dispatchEvent(event);
                return;
            }
        }

        // Generate a unique key for this element's changes
        const changeKey = settingsPanel.type === 'column-element'
            ? `${settingsPanel.sectionId}-${settingsPanel.column}-${settingsPanel.itemId}`
            : settingsPanel.itemId;

        const publicDirectorySettings = isPublicDirectory
            ? {
                scope: tempSettings.scope,
                htmlConfig: tempSettings.htmlConfig ?? {
                    tag: 'div',
                    content: '',
                    attributes: {
                        class: 'public_directory-element'
                    }
                },
                elementClass: tempSettings.elementClass,
                queryPayload: tempSettings.queryPayload ?? tempSettings.publicDirectoryPayload
            }
            : null;



        // Determine which color keys were explicitly changed by the user in this session
        const elementType = settingsPanel.itemType ?? '';
        const colorKeysForType = COLOR_KEYS_BY_TYPE[elementType] ?? [];
        const prevChangedColorKeys: string[] = Array.isArray(initialTempSettingsRef.current._changedColorKeys)
            ? initialTempSettingsRef.current._changedColorKeys as string[]
            : [];
        const newlyChangedColorKeys = colorKeysForType.filter(
            k => tempSettings[k] !== initialTempSettingsRef.current[k]
        );
        const mergedChangedColorKeys = Array.from(new Set([...prevChangedColorKeys, ...newlyChangedColorKeys]));

        // Store the pending changes
        setPendingChanges(prev => {
            const newChanges = new Map(prev);
            const finalSettings = isPublicDirectory
                ? {
                    ...publicDirectorySettings,
                    _meta: {
                        type: settingsPanel.type,
                        itemId: settingsPanel.itemId,
                        itemType: settingsPanel.itemType,
                        sectionId: settingsPanel.sectionId,
                        column: settingsPanel.column,
                        isInnerElement: settingsPanel.type === 'column-element'
                    }
                }
                : {
                    ...tempSettings,
                    _changedColorKeys: mergedChangedColorKeys,
                    _meta: {
                        type: settingsPanel.type,
                        itemId: settingsPanel.itemId,
                        itemType: settingsPanel.itemType,
                        sectionId: settingsPanel.sectionId,
                        column: settingsPanel.column,
                        isInnerElement: settingsPanel.type === 'column-element'
                    }
                };
            
    
            
            newChanges.set(changeKey, finalSettings);
            return newChanges;
        });

        setSettingsPanel(null, undefined, { skipPrompt: true });
    };

    const handleCancelSettings = () => {
        if (settingsPanel?.type === 'section' && canvasItemsSnapshot) {
            setCanvasItems(canvasItemsSnapshot);
        }

        if (shouldBypassCancelPrompt(settingsPanel)) {
            setSettingsPanel(null, undefined, { skipPrompt: true });
            return;
        }

        setSettingsPanel(null);
    };

    const updateTempSetting = (key: string, value: any) => {
        setTempSettings((prev: any) => ({ ...prev, [key]: value }));
    };

    return (
        <>
            <Backdrop isOpen={settingsPanel !== null} onClick={handleCancelSettings} />
            <RightSidebar isOpen={settingsPanel !== null} theme={theme}>
                {settingsPanel && (
                    <>
                        <SidebarHeader>
                            <StyledText fontSize='22px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>
                                {settingsPanel.type === 'section'
                                    ? settingsPanel.itemType === "single_column"
                                        ? 'Single Column Properties'
                                        : 'Multi Column Properties'
                                    : settingsPanel.type === 'header'
                                        ? 'Header Settings'
                                        : settingsPanel.type === 'footer'
                                            ? 'Footer Settings'
                                            : settingsPanel.type === 'element'
                                                ? `${settingsPanel.itemType?.replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())} Properties` : settingsPanel.itemType === "public_directory" ? "Public Directory Properties" :
                                                    `${settingsPanel.itemType?.replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())} Properties`
                                }
                            </StyledText>
                        </SidebarHeader>

                        <SidebarContent>
                            {settingsPanel.type === 'element' && settingsPanel.itemType === 'heading' && (
                                <HeadingProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                            )}

                            {settingsPanel.type === 'element' && settingsPanel.itemType === 'paragraph' && (
                                <ParagraphProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                            )}
                            {settingsPanel.type === 'element' && settingsPanel.itemType === 'separator' && (
                                <SeparatorProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                            )}

                            {settingsPanel.type === 'element' && settingsPanel.itemType === 'button' && (
                                <ButtonProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                            )}

                            {settingsPanel.type === 'element' && settingsPanel.itemType === 'media' && (
                                <MediaProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} onUploadStateChange={setIsMediaUploading} />
                            )}

                            {settingsPanel.type === 'element' && settingsPanel.itemType === 'form' && (
                                <FormProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                            )}
                            {settingsPanel.type === 'element' && settingsPanel.itemType === 'search_query' && (
                                <SearchQueryProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                            )}
                            {settingsPanel.type === 'element' && settingsPanel.itemType === 'public_directory' && (
                                <PublicDirectoryProperties key={`public-directory-${settingsPanel.itemId}`} updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                            )}

                            {settingsPanel.type === 'section' && (
                                <SectionProperties
                                    updateTempSetting={updateTempSetting}
                                    tempSettings={tempSettings}
                                    sectionId={settingsPanel.itemId}
                                    canvasItems={canvasItems}
                                    setCanvasItems={setCanvasItems}
                                    onUploadStateChange={setIsMediaUploading}
                                />
                            )}

                            {settingsPanel.type === 'column-element' && (
                                (settingsPanel.itemType === 'separator' && (
                                    <SeparatorProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                                )) ||
                                (settingsPanel.itemType === 'heading' && (
                                    <HeadingProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                                )) ||
                                (settingsPanel.itemType === 'paragraph' && (
                                    <ParagraphProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                                )) ||
                                (settingsPanel.itemType === 'button' && (
                                    <ButtonProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                                )) ||
                                (
                                    settingsPanel.itemType === 'media' && (
                                        <MediaProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} onUploadStateChange={setIsMediaUploading} />
                                    )
                                ) ||
                                (settingsPanel.itemType === 'form' && (
                                    <FormProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                                )) || (
                                    settingsPanel.itemType === 'search_query' && (
                                        <SearchQueryProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                                    )
                                )
                                || (
                                    settingsPanel.itemType === 'public_directory' && (
                                        <PublicDirectoryProperties key={`public-directory-${settingsPanel.itemId}`} updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                                    )
                                )
                            )}



                            {settingsPanel.type === 'header' && (
                                <HeaderProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                            )}

                            {settingsPanel.type === 'footer' && (
                                <FooterProperties updateTempSetting={updateTempSetting} tempSettings={tempSettings} />
                            )}

                            <div className='d-flex justify-content-end gap-2  mt-4 pt-2'>
                                <Button
                                    theme={theme}
                                    btnBg={theme?.CorporateTheme?.colors?.background}
                                    color={theme?.CorporateTheme?.colors?.textSecondary}
                                    type="button"
                                    $variant="customButton"
                                    className="px-3"
                                    onClick={handleCancelSettings}
                                    borderColor={theme?.CreateFormTheme?.colors?.inputBorders}
                                    disabled={isMediaUploading}
                                >
                                    <CrossIcon />
                                    Cancel
                                </Button>
                                <Button
                                    theme={theme}
                                    btnBg={isMediaUploading ? theme?.CorporateTheme?.colors?.headerColor : theme?.CorporateTheme?.colors?.primary}
                                    color={theme?.CorporateTheme?.colors?.background}
                                    type="button"
                                    $variant="customButton"
                                    className={`px-3`}
                                    borderColor='none'
                                    onClick={handleApplySettings}
                                    radius='3px'
                                    disabled={isMediaUploading}
                                    title={isMediaUploading ? 'Please wait for the upload to complete' : 'Save changes'}
                                >
                                    {/* {isMediaUploading ? (
                                        <>
                                            Uploading...
                                        </>
                                    ) : ( */}
                                        <>
                                            <SaveIcon color={theme.CorporateTheme.colors.background} />
                                            Save
                                        </>
                                    {/* )} */}
                                </Button>
                            </div>
                        </SidebarContent>

                        {/* <SidebarFooter> */}
                        {/* <CancelButton onClick={handleCancelSettings}>
                                Close
                            </CancelButton>
                            <SaveButton onClick={handleApplySettings}>
                                Apply
                            </SaveButton> */}

                        {/* </SidebarFooter> */}
                    </>
                )}
            </RightSidebar>
        </>
    );
};

export default SettingsPanel;